import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import {
    PurchaseOrderDto,
    IPurchaseOrderDto,
    SiteDto,
    PurchaseOrderDataDto,
    GoodsDto,
    GoodsBatchItemDto,
    GoodsStatus,
    ClientDto,
    ArticleDto,
    BatchDto,
    SpecializedOrderStatus,
    OrderDto,
} from '../../api/ClientApi';
import { RootState } from '../reducers';
import { myAssert } from '../../common/console';
import { WarehouseClient } from '../../api/Extensions';

export const createNewEmptyPurchaseOrder = createAction('purchaseOrder/createNewEmptyPurchaseOrder');
export const clearPurchaseOrder = createAction('purchaseOrder/clearPurchaseOrder');

export const setPurchaseOrderDestinationSite = createAction<SiteDto | undefined>(
    'purchaseOrder/setPurchaseOrderDestinationSite',
);
export const removePurchaseOrderDestinationSite = createAction('purchaseOrder/removePurchaseOrderDestinationSite');
export const setPurchaseOrderSupplier = createAction<ClientDto | undefined>('purchaseOrder/setPurchaseOrderSupplier');
export const removePurchaseOrderSupplier = createAction('purchaseOrder/removePurchaseOrderSupplier');
export const updatePurchaseOrderDescription = createAction<string | undefined>(
    'purchaseOrder/updatePurchaseOrderDescription',
);
export const updatePurchaseOrderUniqueBatchId = createAction<string | undefined>(
    'purchaseOrder/updatePurchaseOrderUniqueBatchId',
);
export const updatePurchaseOrderBatchArrivalTime = createAction<Date | undefined>(
    'purchaseOrder/updatePurchaseOrderBatchArrivalTime',
);
export const updatePurchaseOrderDocumentCode = createAction<string | undefined>(
    'purchaseOrder/updatePurchaseOrderDocumentCode',
);
export const updatePurchaseOrderData = createAction<string | undefined>('purchaseOrder/updatePurchaseOrderData');

export const createNewPurchaseOrder = createAction<IPurchaseOrderDto>('purchaseOrder/createNewPurchaseOrder');
export const addOrUpdatePurchaseOrder = createAction<PurchaseOrderDto>('purchaseOrder/addOrUpdatePurchaseOrder');

export const addGoodsToPurchaseOrder = createAction<{
    purchaseOrderId: number;
    purchaseOrderGoods?: GoodsDto;
}>('purchaseOrder/addGoodsToPurchaseOrder');

export const removeGoodsFromPurchaseOrder = createAction<{
    purchaseOrderId: number;
    purchaseOrderGoodsId?: number;
}>('purchaseOrder/removeGoodsFromPurchaseOrder');

export const savePurchaseOrder = createAsyncThunk<PurchaseOrderDto, void>(
    'purchaseOrder/savePurchaseOrder',
    async (payload: void, thunkAPI) => {
        const rootState = thunkAPI.getState() as RootState;
        const purchaseOrderState = rootState.purchaseOrderForm;

        const orderedGoods = rootState.goodsToPurchaseOrderForm.goodBatchEntities;

        const purchaseOrder = purchaseOrderState.purchaseOrder;
        const batchArrivalTime = purchaseOrderState.batchArrivalTime;
        const uniqueBatchId = purchaseOrderState.uniqueBatchId;

        myAssert(() => purchaseOrder !== null);

        if (purchaseOrder === null) {
            throw new Error();
        }

        const purchaseOrderId: number | undefined = purchaseOrder.id;
        let savedPurchaseOrder: PurchaseOrderDto | undefined;

        const newPurchaseOrder = new PurchaseOrderDto();
        newPurchaseOrder.init(purchaseOrder);

        if (!purchaseOrderId || purchaseOrderId <= 0) {
            newPurchaseOrder.goods = [];
            const addPurchaseOrderPromise = WarehouseClient.addNewPurchaseOrder(
                newPurchaseOrder as PurchaseOrderDto,
            ).then(async (resPurchaseOrder) => {
                let savedPurchaseOrderGoodsItems: GoodsDto[] = [];
                const savedPurchaseGoodsPromises: Promise<GoodsDto>[] = [];
                orderedGoods?.map((g) => {
                    const newGood = g.goods;
                    const newBatch = g.batch;

                    const goodBatch = new GoodsBatchItemDto({
                        articleId: newGood.article?.id,
                        batchArrivalTime: batchArrivalTime ?? undefined,
                        batchId: newBatch.id,
                        supplierId: resPurchaseOrder?.supplierId,
                        data: newGood.data,
                        goodsStatus: GoodsStatus.Ordered,
                        purchasePricePerArticle: newBatch.purchasePricePerArticle,
                        quantity: newGood.quantity,
                        siteId: newGood.siteId,
                        uniqueBatchId: uniqueBatchId ?? undefined,
                        purchaseOrderId: resPurchaseOrder?.id,
                    });

                    const addPurchaseGoodsPromise = WarehouseClient.addNewGoods(goodBatch);

                    savedPurchaseGoodsPromises.push(addPurchaseGoodsPromise);
                });

                savedPurchaseOrderGoodsItems = await Promise.all(savedPurchaseGoodsPromises);

                resPurchaseOrder.goods = savedPurchaseOrderGoodsItems;

                return resPurchaseOrder;
            });

            savedPurchaseOrder = await addPurchaseOrderPromise;
        } else {
            myAssert(() => newPurchaseOrder.id !== undefined);

            if (newPurchaseOrder.id === undefined) {
                throw new Error();
            }

            const filteredPurchaseGoods = newPurchaseOrder.goods;

            let savedPurchaseGoods: GoodsDto[] = [];
            let removedGoods: number[] = [];

            const savedPurchaseGoodsPromises: Promise<GoodsDto>[] = [];
            const removedPurchaseGoodsIdsPromises: Promise<number>[] = [];

            // go through oldGoods;
            filteredPurchaseGoods?.map((fg) => {
                // check if this goods exists in new purchaseOrder
                const purchaseOrderedGoods = orderedGoods?.find((bg) => bg.goods.article?.id === fg.article?.id);

                if (!!purchaseOrderedGoods) {
                    // if quantity is different, update existing goods quantity
                    if (purchaseOrderedGoods.goods.quantity !== fg.quantity) {
                        fg.quantity = purchaseOrderedGoods.goods.quantity;
                        fg.purchaseOrder = new PurchaseOrderDto(purchaseOrder);

                        const newBatch = new BatchDto({
                            ...purchaseOrderedGoods.batch,
                            purchasePricePerArticle: purchaseOrderedGoods.batch.purchasePricePerArticle,
                            supplier: newPurchaseOrder.supplier ?? undefined,
                            batchArrivalTime: batchArrivalTime ?? undefined,
                            uniqueBatchId: uniqueBatchId ?? undefined,
                        });

                        fg.batch = newBatch;

                        const addPurchaseGoodsPromise = WarehouseClient.updateGoods(fg.id ?? 0, fg);

                        savedPurchaseGoodsPromises.push(addPurchaseGoodsPromise);
                    } else {
                        const newBatch = new BatchDto({
                            ...purchaseOrderedGoods.batch,
                            supplier: newPurchaseOrder.supplier ?? undefined,
                            batchArrivalTime: batchArrivalTime ?? undefined,
                            uniqueBatchId: uniqueBatchId ?? undefined,
                        });

                        WarehouseClient.updateBatch(newBatch.id ?? 0, newBatch).then((resBatch) => {
                            fg.batch = resBatch;
                            thunkAPI.dispatch(addGoodsToPurchaseOrder({ purchaseOrderId, purchaseOrderGoods: fg }));
                        });
                    }
                } else {
                    // this goods does not exist in new purchaseOrder, so we have to delete it and disconnect it from the purchaseOrder
                    const removedPurchaseOrderGoodsItemsIdsPromise = WarehouseClient.deleteGoods(fg.id ?? 0).then(
                        () => {
                            thunkAPI.dispatch(
                                removeGoodsFromPurchaseOrder({
                                    purchaseOrderId: newPurchaseOrder.id ?? 0,
                                    purchaseOrderGoodsId: fg.id,
                                }),
                            );
                            return fg.id ?? 0;
                        },
                    );
                    removedPurchaseGoodsIdsPromises.push(removedPurchaseOrderGoodsItemsIdsPromise);
                }
            });

            orderedGoods
                ?.filter((fbg) => !filteredPurchaseGoods?.find((fg) => fg.article?.id === fbg.goods.article?.id))
                .map((fbg) => {
                    // in this case, goods does not exist in old purchaseOrder, so we have to add id and connect it to the purchaseOrder
                    const newGood = fbg.goods;
                    const newBatch = fbg.batch;

                    const newPurchaseOrderGood = new GoodsBatchItemDto({
                        articleId: newGood.article?.id,
                        batchArrivalTime: batchArrivalTime ?? undefined,
                        batchId: newBatch.id,
                        supplierId: newPurchaseOrder.supplierId,
                        data: newGood.data,
                        goodsStatus: GoodsStatus.Ordered,
                        purchasePricePerArticle: newBatch.purchasePricePerArticle,
                        quantity: newGood.quantity,
                        siteId: newGood.siteId,
                        uniqueBatchId: uniqueBatchId ?? undefined,
                        purchaseOrderId: purchaseOrderId,
                    });

                    const addPurchaseOrderGoodsItemPromise = WarehouseClient.addNewGoods(newPurchaseOrderGood).then(
                        (res) => {
                            thunkAPI.dispatch(addGoodsToPurchaseOrder({ purchaseOrderId, purchaseOrderGoods: res }));
                            return res;
                        },
                    );

                    savedPurchaseGoodsPromises.push(addPurchaseOrderGoodsItemPromise);
                });

            savedPurchaseGoods = await Promise.all(savedPurchaseGoodsPromises);

            removedGoods = await Promise.all(removedPurchaseGoodsIdsPromises);

            newPurchaseOrder.goods = [];

            const addPurchaseOrderPromise = WarehouseClient.updatePurchaseOrder(purchaseOrderId, newPurchaseOrder);

            savedPurchaseOrder = await addPurchaseOrderPromise;
        }

        return savedPurchaseOrder;
    },
    {
        condition: (payload: void, thunkAPI: { getState: any }) => {
            const purchaseOrderState = (thunkAPI.getState() as RootState).purchaseOrderForm;

            return purchaseOrderState.purchaseOrder !== null;
        },
    },
);

export const changePurchaseOrderStatus = createAsyncThunk<PurchaseOrderDto | undefined, SpecializedOrderStatus>(
    'purchaseOrder/changePurchaseOrderStatus',
    async (payload: SpecializedOrderStatus, thunkAPI) => {
        const rootState = thunkAPI.getState() as RootState;
        const purchaseOrderState = rootState.purchaseOrderForm;

        const purchaseOrder = purchaseOrderState.purchaseOrder;

        const newStatus = payload;

        myAssert(() => purchaseOrder !== null);

        if (purchaseOrder === null) {
            throw new Error();
        }

        const purchaseOrderId: number | undefined = purchaseOrder.id;
        let savedPurchaseOrder: PurchaseOrderDto | undefined;

        const newPurchaseOrder = new PurchaseOrderDto();
        newPurchaseOrder.init(purchaseOrder);

        if (!!purchaseOrderId && purchaseOrderId > 0) {
            newPurchaseOrder.goods = [];
            newPurchaseOrder.purchaseOrderStatus = newStatus;

            const addPurchaseOrderPromise = WarehouseClient.updatePurchaseOrder(
                purchaseOrderId,
                newPurchaseOrder as PurchaseOrderDto,
            );

            savedPurchaseOrder = await addPurchaseOrderPromise;
        }

        return savedPurchaseOrder;
    },
    {
        condition: (payload: SpecializedOrderStatus, thunkAPI: { getState: any }) => {
            const purchaseOrderState = (thunkAPI.getState() as RootState).purchaseOrderForm;

            return purchaseOrderState.purchaseOrder !== null;
        },
    },
);

export const importPurchaseOrders = createAsyncThunk<PurchaseOrderDataDto>(
    'purchaseOrder/importPurchaseOrders',
    async (thunkAPI) => {
        return WarehouseClient.getAllPurchaseOrders();
    },
    {
        condition: (payload: void, thunkAPI: { getState: any }) => {
            const purchaseOrdersState = (thunkAPI.getState() as RootState).purchaseOrders;

            return !purchaseOrdersState.purchaseOrdersFetchActive;
        },
    },
);

export const refreshPurchaseOrdersData = createAsyncThunk<void, void>(
    'purchaseOrder/refreshPurchaseOrdersData',
    async (payload: void, thunkAPI) => {
        const rootState = thunkAPI.getState() as RootState;
        const purchaseOrdersState = rootState.purchaseOrders;

        return WarehouseClient.getPurchaseOrdersDataRefreshInfo().then((info) => {
            if (info.infoDate && info.infoDate >= purchaseOrdersState!.purchaseOrdersInfoDate!) {
                thunkAPI.dispatch(importPurchaseOrders());
            }
        });
    },
    {
        condition: (payload: void, thunkAPI: { getState: any }) => {
            const purchaseOrdersState = (thunkAPI.getState() as RootState).purchaseOrders;

            if (purchaseOrdersState.purchaseOrdersInfoDate === null) {
                return false;
            }

            const purchaseOrderInfoDate: Date = new Date(purchaseOrdersState.purchaseOrdersInfoDate);
            purchaseOrderInfoDate.setMinutes(purchaseOrderInfoDate.getMinutes() + 1);

            return purchaseOrderInfoDate < new Date();
        },
    },
);

export const importPurchaseOrder = createAsyncThunk<PurchaseOrderDto, number>(
    'purchaseOrder/importPurchaseOrder',
    async (payload, thunkAPI) => {
        return WarehouseClient.getPurchaseOrder(payload);
    },
);

export const importPurchaseOrderForPurchaseOrderDetails = createAsyncThunk<PurchaseOrderDto, number>(
    'purchaseOrder/importPurchaseOrderForPurchaseOrderDetails',
    async (payload, thunkAPI) => {
        return WarehouseClient.getPurchaseOrder(payload);
    },
);

export const clearPurchaseOrderForPurchaseOrderDetails = createAction(
    'purchaseOrder/clearPurchaseOrderForPurchaseOrderDetails',
);

export const updatePurchaseOrderForPurchaseOrderDetails = createAction<PurchaseOrderDto>(
    'purchaseOrder/updatePurchaseOrderForPurchaseOrderDetails',
);

export const removePurchaseOrderForPurchaseOrderDetails = createAction(
    'purchaseOrder/removePurchaseOrderForPurchaseOrderDetails',
);

export const setGoodsBatchEntitiesForPurchaseOrder = createAction<{ goods: GoodsDto[]; receptionOrders: OrderDto[] }>(
    'purchaseOrder/setGoodsBatchEntitiesForPurchaseOrder',
);
export const createNewEmptyGoodsBatchEntityForPurchaseOrder = createAction(
    'purchaseOrder/createNewEmptyGoodsBatchEntityForPurchaseOrder',
);
export const removeGoodsBatchEntityForPurchaseOrder = createAction<number>(
    'purchaseOrder/removeGoodsBatchEntityForPurchaseOrder',
);
export const clearGoodsForPurchaseOrder = createAction('purchaseOrder/clearGoodsForPurchaseOrder');

export const updateGoodsArticleForPurchaseOrder = createAction<{ index: number; article?: ArticleDto }>(
    'purchaseOrder/updateGoodsArticleForPurchaseOrder',
);
export const updateGoodsStatusForPurchaseOrder = createAction<{ index: number; goodsStatus?: GoodsStatus }>(
    'purchaseOrder/updateGoodsStatusForPurchaseOrder',
);
export const updateGoodsQuantityForPurchaseOrder = createAction<{ index: number; quantity?: number }>(
    'purchaseOrder/updateGoodsQuantityForPurchaseOrder',
);
export const updateGoodsSiteIdForPurchaseOrder = createAction<{ index: number; siteId?: number }>(
    'purchaseOrder/updateGoodsSiteIdForPurchaseOrder',
);
export const updateGoodsSiteForPurchaseOrder = createAction<{ index: number; site?: SiteDto }>(
    'purchaseOrder/updateGoodsSiteForPurchaseOrder',
);

export const updateBatchPriceForPurchaseOrder = createAction<{ index: number; batchPrice?: number }>(
    'purchaseOrder/updateBatchPriceForPurchaseOrder',
);
export const updateBatchUniqueIdForPurchaseOrder = createAction<{ index: number; uniqueBatchId?: string }>(
    'purchaseOrder/updateBatchUniqueIdForPurchaseOrder',
);
export const updateBatchClientForPurchaseOrder = createAction<{ index: number; batchClient?: ClientDto }>(
    'purchaseOrder/updateBatchClientForPurchaseOrder',
);
export const updateBatchArrivalTimeForPurchaseOrder = createAction<{ index: number; batchArrivalTime?: Date }>(
    'purchaseOrder/updateBatchArrivalTimeForPurchaseOrder',
);
